import { CheckCircle, Download, Calendar, Mail, ArrowRight } from 'lucide-react';
import { BookingData } from './BookingFlow';
import { usePets } from '../../context/PetsContext';

interface PaymentSuccessProps {
  bookingData: BookingData;
  totalAmount: number;
  onViewAppointment: () => void;
  onClose: () => void;
}

export function PaymentSuccess({ 
  bookingData, 
  totalAmount, 
  onViewAppointment, 
  onClose 
}: PaymentSuccessProps) {
  const { pets } = usePets();
  const selectedPetsData = pets.filter(pet => bookingData.selectedPets.includes(pet.id));
  const bookingReference = `BK-${Date.now().toString().slice(-8)}`;
  const paymentReference = `PAY-${Date.now().toString().slice(-10)}`;

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#F7F8FA' }}>
      <div className="w-full max-w-2xl">
        <div className="p-8 text-center" style={{ backgroundColor: '#FFFFFF', borderRadius: '24px', border: '1px solid #E2DFEA', boxShadow: '0 12px 32px rgba(89, 75, 255, 0.08)' }}>
          {/* Success Icon */}
          <div className="mb-6 flex justify-center">
            <div 
              className="w-24 h-24 flex items-center justify-center animate-bounce"
              style={{ 
                background: 'linear-gradient(135deg, #10B981 0%, #34D399 100%)',
                borderRadius: '50%'
              }}
            >
              <CheckCircle className="w-12 h-12 text-white" />
            </div>
          </div>

          {/* Success Message */}
          <h2 style={{ fontSize: '2rem', fontFamily: 'Sora, sans-serif', fontWeight: 700, color: '#2D1F7B', marginBottom: '0.5rem' }}>
            🎉 Payment Successful!
          </h2>
          <p style={{ fontSize: '1.125rem', fontFamily: 'Inter, sans-serif', color: '#464452', marginBottom: '2rem' }}>
            Your appointment is confirmed and payment has been processed
          </p>

          {/* Payment Details Card */}
          <div className="mb-6 p-6 text-left" style={{ backgroundColor: '#D1FAE5', borderRadius: '16px', border: '1px solid #10B981' }}>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p style={{ fontSize: '0.875rem', color: '#065F46', fontFamily: 'Inter, sans-serif', marginBottom: '0.25rem' }}>
                  {totalAmount > 0 ? 'Amount Paid' : 'Booking Status'}
                </p>
                <p style={{ fontSize: totalAmount > 0 ? '1.5rem' : '1rem', fontFamily: 'Sora, sans-serif', fontWeight: 700, color: '#065F46' }}>
                  {totalAmount > 0 ? `$${totalAmount.toFixed(2)}` : 'Confirmed'}
                </p>
              </div>
              <div>
                <p style={{ fontSize: '0.875rem', color: '#065F46', fontFamily: 'Inter, sans-serif', marginBottom: '0.25rem' }}>
                  Payment Method
                </p>
                <p style={{ fontSize: '1.125rem', fontFamily: 'Sora, sans-serif', fontWeight: 600, color: '#065F46' }}>
                  •••• 4242
                </p>
              </div>
            </div>
          </div>

          {/* Booking Details */}
          <div className="mb-6 p-6 text-left space-y-4" style={{ backgroundColor: '#F7F8FA', borderRadius: '16px', border: '1px solid #E2DFEA' }}>
            <div>
              <p style={{ fontSize: '0.875rem', color: '#464452', fontFamily: 'Inter, sans-serif', marginBottom: '0.5rem' }}>
                Booking Reference
              </p>
              <p style={{ fontSize: '1.125rem', fontFamily: 'Sora, sans-serif', fontWeight: 700, color: '#2D1F7B' }}>
                {bookingReference}
              </p>
            </div>

            <div className="border-t pt-4" style={{ borderColor: '#E2DFEA' }}>
              <p style={{ fontSize: '0.875rem', color: '#464452', fontFamily: 'Inter, sans-serif', marginBottom: '0.5rem' }}>
                Professional
              </p>
              <p style={{ fontSize: '1rem', fontFamily: 'Sora, sans-serif', fontWeight: 600, color: '#2D1F7B' }}>
                {bookingData.professional?.name}
              </p>
            </div>

            <div className="border-t pt-4" style={{ borderColor: '#E2DFEA' }}>
              <p style={{ fontSize: '0.875rem', color: '#464452', fontFamily: 'Inter, sans-serif', marginBottom: '0.5rem' }}>
                Pet(s)
              </p>
              <div className="flex flex-wrap gap-2">
                {selectedPetsData.map(pet => (
                  <span 
                    key={pet.id}
                    className="px-3 py-1"
                    style={{ 
                      backgroundColor: '#F0EEFF', 
                      color: '#594BFF', 
                      borderRadius: '8px',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '0.875rem',
                      fontWeight: 600
                    }}
                  >
                    {pet.species === 'dog' ? '🐕' : '🐈'} {pet.name}
                  </span>
                ))}
              </div>
            </div>

            <div className="border-t pt-4" style={{ borderColor: '#E2DFEA' }}>
              <p style={{ fontSize: '0.875rem', color: '#464452', fontFamily: 'Inter, sans-serif', marginBottom: '0.5rem' }}>
                Date & Time
              </p>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" style={{ color: '#594BFF' }} />
                <span style={{ fontSize: '1rem', fontFamily: 'Inter, sans-serif', color: '#2D1F7B' }}>
                  {bookingData.appointmentTime?.date.toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })} at {bookingData.appointmentTime?.time}
                </span>
              </div>
            </div>

            <div className="border-t pt-4" style={{ borderColor: '#E2DFEA' }}>
              <p style={{ fontSize: '0.875rem', color: '#464452', fontFamily: 'Inter, sans-serif', marginBottom: '0.5rem' }}>
                Transaction ID
              </p>
              <p style={{ fontSize: '0.875rem', fontFamily: 'mono', color: '#2D1F7B' }}>
                {paymentReference}
              </p>
            </div>
          </div>

          {/* What Happens Next */}
          <div className="mb-6 p-6 text-left" style={{ backgroundColor: '#DBEAFE', borderRadius: '16px', border: '1px solid #3B82F6' }}>
            <h3 style={{ fontSize: '1.125rem', fontFamily: 'Sora, sans-serif', fontWeight: 700, color: '#1E40AF', marginBottom: '1rem' }}>
              What Happens Next?
            </h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#3B82F6', borderRadius: '50%' }}>
                  <Mail className="w-3 h-3 text-white" />
                </div>
                <div>
                  <p style={{ fontSize: '0.875rem', fontFamily: 'Inter, sans-serif', color: '#1E40AF', fontWeight: 600 }}>
                    Email Confirmation Sent
                  </p>
                  <p style={{ fontSize: '0.75rem', fontFamily: 'Inter, sans-serif', color: '#1E40AF' }}>
                    Check your inbox for booking details and receipt
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#3B82F6', borderRadius: '50%' }}>
                  <Calendar className="w-3 h-3 text-white" />
                </div>
                <div>
                  <p style={{ fontSize: '0.875rem', fontFamily: 'Inter, sans-serif', color: '#1E40AF', fontWeight: 600 }}>
                    Professional Notified
                  </p>
                  <p style={{ fontSize: '0.75rem', fontFamily: 'Inter, sans-serif', color: '#1E40AF' }}>
                    {bookingData.professional?.name} has been informed of your booking
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#3B82F6', borderRadius: '50%' }}>
                  <CheckCircle className="w-3 h-3 text-white" />
                </div>
                <div>
                  <p style={{ fontSize: '0.875rem', fontFamily: 'Inter, sans-serif', color: '#1E40AF', fontWeight: 600 }}>
                    Blockchain Confirmation
                  </p>
                  <p style={{ fontSize: '0.75rem', fontFamily: 'Inter, sans-serif', color: '#1E40AF' }}>
                    Booking recorded on Solana for tamper-proof verification
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={onViewAppointment}
              className="w-full flex items-center justify-center gap-2 py-4 text-white transition-all"
              style={{
                background: 'linear-gradient(135deg, #594BFF 0%, #8B7FFF 100%)',
                borderRadius: '16px',
                fontFamily: 'Sora, sans-serif',
                fontWeight: 700,
                fontSize: '1.125rem'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.opacity = '0.9';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.opacity = '1';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              View Appointment Details
              <ArrowRight className="w-5 h-5" />
            </button>

            <button
              className="w-full flex items-center justify-center gap-2 py-4 transition-all"
              style={{
                backgroundColor: '#FFFFFF',
                border: '2px solid #E2DFEA',
                color: '#594BFF',
                borderRadius: '16px',
                fontFamily: 'Sora, sans-serif',
                fontWeight: 700,
                fontSize: '1.125rem'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.borderColor = '#594BFF';
                e.currentTarget.style.backgroundColor = '#F0EEFF';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.borderColor = '#E2DFEA';
                e.currentTarget.style.backgroundColor = '#FFFFFF';
              }}
            >
              <Download className="w-5 h-5" />
              Download Receipt
            </button>

            <button
              onClick={onClose}
              className="w-full py-3 transition-colors"
              style={{
                color: '#464452',
                fontFamily: 'Sora, sans-serif',
                fontWeight: 600
              }}
              onMouseOver={(e) => e.currentTarget.style.color = '#2D1F7B'}
              onMouseOut={(e) => e.currentTarget.style.color = '#464452'}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
